import { supabase } from './client';
import { Database } from './types';

type SavedResult = Database['public']['Tables']['saved_results']['Insert'];
type EmailSubscription = Database['public']['Tables']['email_subscriptions']['Insert'];

// Helper function to try both production URLs
async function fetchWithFallback(endpoint: string, options: RequestInit) {
  const isProd = import.meta.env.PROD;
  const primaryUrl = import.meta.env.VITE_API_URL;
  const fallbackUrl = 'https://allera-production.up.railway.app';

  try {
    // In development, just use the development URL
    if (!isProd) {
      const response = await fetch(`${primaryUrl}${endpoint}`, options);
      return response;
    }

    // In production, try primary URL first
    try {
      const response = await fetch(`${primaryUrl}${endpoint}`, options);
      if (response.ok) return response;
    } catch (error) {
      console.log('Primary URL failed, trying fallback...');
    }

    // If primary fails, try fallback
    const response = await fetch(`${fallbackUrl}${endpoint}`, options);
    return response;
  } catch (error) {
    throw new Error('Failed to fetch from both URLs');
  }
}

export async function saveResults(email: string, userData: any) {
  try {
    const response = await fetchWithFallback('/api/save-results', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        userData,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to save results');
    }

    return result;
  } catch (error) {
    console.error('Error saving results:', error);
    return {
      success: false,
      error: 'Failed to save results',
    };
  }
}

export async function getResultsByHardlink(hardlink: string) {
  try {
    // First get the saved result
    const { data: savedResult, error: savedResultError } = await supabase
      .from('saved_results')
      .select('*')
      .eq('hardlink', hardlink)
      .single();

    if (savedResultError) throw savedResultError;

    if (!savedResult) {
      return {
        success: false,
        error: 'No results found for this link'
      };
    }

    // Get the supplements data based on the saved recommendation IDs
    const recommendationIds = (savedResult.user_data as any).recommendationIds.map((rec: any) => rec.id);
    
    const { data: supplements, error: supplementsError } = await supabase
      .from('supplements')
      .select(`
        *,
        benefits:supplement_benefits(
          benefit,
          benefit_long,
          supplement_id
        ),
        goals:supplement_goals(
          goal,
          supplement_id
        )
      `)
      .in('id', recommendationIds);

    if (supplementsError) throw supplementsError;

    // Combine the saved user-specific data with the current supplement data
    const recommendations = supplements.map(supplement => {
      const savedRec = (savedResult.user_data as any).recommendationIds.find(
        (rec: any) => rec.id === supplement.id
      );
      
      return {
        id: supplement.id,
        supplementId: supplement.id,
        name: supplement.name,
        priority: savedRec.priority,
        reason: savedRec.reason,
        notes: savedRec.notes,
        dosage_male: supplement.dosage_male,
        dosage_female: supplement.dosage_female,
        how_to_take: supplement.how_to_take,
        how_to_take_simple: supplement.how_to_take_simple,
        precautions: supplement.precautions,
        benefits: supplement.benefits?.map((b: any) => b.benefit) || [],
        benefitsLong: supplement.benefits?.reduce((acc: any, b: any) => {
          if (b.benefit_long) acc[b.benefit] = b.benefit_long;
          return acc;
        }, {}) || {},
        description: supplement.description,
        full_description: supplement.full_description,
        type: supplement.type,
        rating: supplement.rating,
        matchConfidence: 1 // Since this was a saved match, we'll set this to 1
      };
    });

    return {
      success: true,
      data: {
        userData: (savedResult.user_data as any).userData,
        recommendations
      }
    };
  } catch (error) {
    console.error('Error fetching results:', error);
    return {
      success: false,
      error: 'Failed to fetch results'
    };
  }
}

export async function getResultsByEmail(email: string) {
  try {
    // First get the email subscription
    const { data: subscription, error: subscriptionError } = await supabase
      .from('email_subscriptions')
      .select('last_saved_result_id')
      .eq('email', email)
      .eq('is_active', true)
      .single();

    if (subscriptionError) throw subscriptionError;

    if (!subscription?.last_saved_result_id) {
      return {
        success: false,
        error: 'No saved results found for this email'
      };
    }

    // Get the saved result using the ID from the subscription
    const { data: savedResult, error: savedResultError } = await supabase
      .from('saved_results')
      .select('*')
      .eq('id', subscription.last_saved_result_id)
      .single();

    if (savedResultError) throw savedResultError;

    if (!savedResult) {
      return {
        success: false,
        error: 'No results found for this email'
      };
    }

    // Get the supplements data based on the saved recommendation IDs
    const recommendationIds = (savedResult.user_data as any).recommendationIds.map((rec: any) => rec.id);
    
    const { data: supplements, error: supplementsError } = await supabase
      .from('supplements')
      .select(`
        *,
        benefits:supplement_benefits(
          benefit,
          benefit_long,
          supplement_id
        ),
        goals:supplement_goals(
          goal,
          supplement_id
        )
      `)
      .in('id', recommendationIds);

    if (supplementsError) throw supplementsError;

    // Combine the saved user-specific data with the current supplement data
    const recommendations = supplements.map(supplement => {
      const savedRec = (savedResult.user_data as any).recommendationIds.find(
        (rec: any) => rec.id === supplement.id
      );
      
      return {
        id: supplement.id,
        supplementId: supplement.id,
        name: supplement.name,
        priority: savedRec.priority,
        reason: savedRec.reason,
        notes: savedRec.notes,
        dosage_male: supplement.dosage_male,
        dosage_female: supplement.dosage_female,
        how_to_take: supplement.how_to_take,
        how_to_take_simple: supplement.how_to_take_simple,
        precautions: supplement.precautions,
        benefits: supplement.benefits?.map((b: any) => b.benefit) || [],
        benefitsLong: supplement.benefits?.reduce((acc: any, b: any) => {
          if (b.benefit_long) acc[b.benefit] = b.benefit_long;
          return acc;
        }, {}) || {},
        description: supplement.description,
        full_description: supplement.full_description,
        type: supplement.type,
        rating: supplement.rating,
        matchConfidence: 1 // Since this was a saved match, we'll set this to 1
      };
    });

    return {
      success: true,
      data: {
        userData: (savedResult.user_data as any).userData,
        recommendations,
        hardlink: savedResult.hardlink
      }
    };
  } catch (error) {
    console.error('Error fetching results:', error);
    return {
      success: false,
      error: 'Failed to fetch results'
    };
  }
} 